import * as React from "react";
import { ComponentPropsWithoutRef } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { FaEllipsisV } from "@react-icons/all-files/fa/FaEllipsisV";
import styled from "@emotion/styled";
import { IconButton } from "@teachers/ds-button";
import { designTokens, easelColors } from "../../helpers/constants";

export interface IMenuOption<T = {}> {
  text: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  props?: T;
}

const DropdownMenu = styled(Dropdown.Menu)({
  padding: 0,
  transform: `translateY(${designTokens["ds-space-half-x"]})`,
});

const DefaultDropdownItem = styled(Dropdown.Item)({
  "&:hover": {
    backgroundColor: easelColors.easelPeriwinkle100,
  },
  "&:focus": {
    backgroundColor: easelColors.easelPeriwinkle200,
  },
  "&:active": {
    backgroundColor: easelColors.easelPeriwinkle200,
    color: designTokens["ds-color-gray-800"],
  },
});

export interface IMoreMenuProps<T = {}> {
  MenuItem?: typeof DefaultDropdownItem;
  menuOptions: IMenuOption<T>[];
}

export const MoreMenuToggleButton = React.forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<"button">
>((props, ref) => {
  const { children, onClick } = props;

  return (
    <IconButton
      ref={ref}
      kind="tertiaryLink"
      size="md"
      onClick={(event: React.MouseEvent) => {
        // Required check because of https://github.com/react-bootstrap/react-bootstrap/issues/4819
        if (onClick) {
          event.preventDefault();
          onClick(event as React.MouseEvent<HTMLButtonElement, MouseEvent>);
        }
      }}
      id="more-menu-toggle-button"
      data-testid="more-menu-toggle-button"
      aria-label="more-menu-toggle-button"
    >
      {children}
    </IconButton>
  );
});

MoreMenuToggleButton.displayName = "MoreMenuToggleButton";

export const MoreMenu = <T,>(props: IMoreMenuProps<T>) => {
  const { menuOptions, MenuItem } = props;
  const Item = MenuItem || DefaultDropdownItem;

  return (
    <Dropdown data-testid="dropdown">
      <Dropdown.Toggle as={MoreMenuToggleButton}>
        <FaEllipsisV />
      </Dropdown.Toggle>

      <DropdownMenu alignRight>
        {menuOptions.map((menuOption: IMenuOption, index: number) => (
          <Item
            key={`${menuOption.text.replace(" ", "")}`}
            eventKey={`${index}`}
            onClick={menuOption.onClick}
            {...menuOption.props}
          >
            {menuOption.text}
          </Item>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};
